'use client';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';

import { SPACE, RADIUS, SHADOW, TEXT_WRAP } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';
import { TrialGiftIcon } from 'src/components/cmui/plan-upgrade/trial-gift-icon';

import { SERVICE_POPUP_ICON, SERVICE_POPUP_INSTAGRAM_ICON } from './service-popup-constants';

import type { ServicePopupKind, ServicePopupCopy } from './service-popup';

/**
 * **서비스 팝업** — 가운데 창 한 장: 머리(그림 · 제목 · 한 줄) · 갈래마다 상자 · 단추 둘(+ 초기 설정 장의 「다시 보지 않기」).
 *
 * ```
 *   체험 끝나 감     체험이 3일 남았어요 · 11월 2일에 끝나요. · 회색 상자(선물 · 결제 혜택 · 지금 결제하면 무료 한달 추가)   [닫기] [혜택 받고 결제하기]
 *   초기 설정 안 함  아직 설정한 것이 없어요 · 한 줄 · 테두리 상자(번호 · 이름 · 회색 한 줄 × 4)                              [나중에 할게요] [초기 설정하기] · 다시 보지 않기
 *   계정 연동 끊김   인스타그램 연동이 끊겼어요 · 스토리 수집이 멈췄어요. · 테두리 상자(인스타그램 · @계정 × 2 · 외 N개)           [닫기] [다시 연동하기]
 * ```
 * 바깥 · Esc · 오른쪽 위 ✕ 는 닫기와 같다. 좁은 화면에서는 양옆 16px 만 남기고 창 안이 스크롤된다.
 */
export function ServicePopupDialog({
  kind,
  copy,
  onClose,
  onAct,
  onNever,
}: {
  kind: ServicePopupKind | null;
  copy: ServicePopupCopy | null;
  onClose: () => void;
  onAct: () => void;
  onNever: () => void;
}) {
  if (!kind || !copy) return null;
  const icon = SERVICE_POPUP_ICON[kind];
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth scroll="paper" aria-labelledby="service-popup-title" slotProps={{ paper: { sx: PAPER_SX } }}>
      <IconButton aria-label="닫기" onClick={onClose} sx={CLOSE_SX}>
        <Iconify icon="mingcute:close-line" width={20} />
      </IconButton>

      <DialogContent sx={CONTENT_SX}>
        <Stack spacing={{ xs: SPACE.card, sm: SPACE.section }}>
          <Stack spacing={{ xs: SPACE.row, sm: SPACE.dense }}>
            <Box sx={{ ...ICON_TILE_SX, bgcolor: icon.bg, color: icon.color }}>
              <Iconify icon={icon.icon} sx={{ width: { xs: 20, sm: 24 }, height: { xs: 20, sm: 24 } }} />
            </Box>
            <Typography id="service-popup-title" variant="h4" component="h2" sx={TITLE_SX}>
              {copy.title}
            </Typography>
            {copy.body && (
              <Typography color="text.secondary" sx={BODY_SX}>
                {copy.body}
              </Typography>
            )}
          </Stack>

          {copy.offer && (
            <Stack direction="row" spacing={{ xs: SPACE.row, sm: SPACE.dense }} alignItems="center" sx={OFFER_SX}>
              <Box sx={GIFT_TILE_SX}>
                <TrialGiftIcon size={20} />
              </Box>
              <Stack spacing={SPACE.tight}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark' }}>{copy.offer.label}</Typography>
                <Typography sx={OFFER_TITLE_SX}>{copy.offer.title}</Typography>
              </Stack>
            </Stack>
          )}

          {copy.steps.length > 0 && (
            <Box sx={LIST_SX}>
              {copy.steps.map((step, index) => (
                <Box key={step.name}>
                  {index > 0 && <Divider />}
                  <Stack direction="row" spacing={SPACE.dense} alignItems="center" sx={ROW_SX}>
                    <Box sx={NUMBER_SX}>{index + 1}</Box>
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{step.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{step.hint}</Typography>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Box>
          )}

          {copy.accounts.shown.length > 0 && (
            <Box sx={LIST_SX}>
              {copy.accounts.shown.map((account, index) => (
                <Box key={account}>
                  {index > 0 && <Divider />}
                  <Stack direction="row" spacing={SPACE.dense} alignItems="center" sx={ROW_SX}>
                    <Box sx={CHANNEL_SX}>
                      <Iconify icon={SERVICE_POPUP_INSTAGRAM_ICON} width={18} />
                    </Box>
                    <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>@{account}</Typography>
                  </Stack>
                </Box>
              ))}
              {copy.accounts.more > 0 && (
                <>
                  <Divider />
                  <Typography variant="body2" color="text.secondary" sx={ROW_SX}>외 {copy.accounts.more}개</Typography>
                </>
              )}
            </Box>
          )}

          <Stack spacing={SPACE.row}>
            <Stack direction="row" spacing={SPACE.row}>
              <Button size="large" variant="outlined" color="inherit" onClick={onClose} sx={CLOSE_BUTTON_SX}>
                {copy.close}
              </Button>
              <Button size="large" variant="contained" color="inherit" onClick={onAct} sx={PRIMARY_BUTTON_SX}>
                {copy.primary.label}
              </Button>
            </Stack>
            {copy.never && (
              <Button color="inherit" onClick={onNever} sx={NEVER_SX}>
                {copy.never}
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 창 — 좁은 화면에서도 양옆 16px 만 남기고, 넘치면 안이 스크롤된다 */
const PAPER_SX = {
  position: 'relative',
  borderRadius: RADIUS.lg,
  boxShadow: SHADOW.overlay,
  m: { xs: SPACE.card, sm: SPACE.block },
  width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
  maxWidth: 440,
  maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
} as const;

/** 안쪽 여백 — 폰은 24 · 넓으면 32 */
const CONTENT_SX = { p: { xs: SPACE.section, sm: SPACE.block } } as const;
const CLOSE_SX = { position: 'absolute', top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 12 }, zIndex: 1, width: 44, height: 44, color: 'text.secondary' } as const;
const ICON_TILE_SX = { width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, borderRadius: RADIUS.md, display: 'flex', alignItems: 'center', justifyContent: 'center' } as const;
const TITLE_SX = { fontWeight: 700, fontSize: { xs: 20, sm: 24 }, lineHeight: 1.35, ...TEXT_WRAP.title } as const;
const BODY_SX = { typography: { xs: 'body2', sm: 'body1' }, ...TEXT_WRAP.body } as const;
/** 혜택 상자 — 선물 · 작은 이름 · 한 줄 */
const GIFT_TILE_SX = { width: 36, height: 36, flexShrink: 0, borderRadius: RADIUS.sm, bgcolor: 'common.white', display: 'flex', alignItems: 'center', justifyContent: 'center' } as const;
const OFFER_SX = { p: { xs: SPACE.dense, sm: SPACE.card }, borderRadius: RADIUS.md, bgcolor: 'grey.100' } as const;
const OFFER_TITLE_SX = { typography: { xs: 'subtitle2', sm: 'subtitle1' }, fontWeight: 600, ...TEXT_WRAP.body } as const;
const LIST_SX = { border: 1, borderColor: 'divider', borderRadius: RADIUS.md } as const;
const ROW_SX = { px: { xs: SPACE.dense, sm: SPACE.card }, py: 1.5 } as const;
const NUMBER_SX = {
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: RADIUS.pill,
  bgcolor: 'primary.lighter',
  color: 'primary.darker',
  typography: 'body2',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;
const CHANNEL_SX = { ...NUMBER_SX, bgcolor: 'grey.100', color: 'text.secondary' } as const;
/** 단추 둘 — 닫기는 글자만큼(줄지 않는다) · 이끄는 단추가 남은 폭을 다 쓴다 · 둘 다 한 줄 */
const BUTTON_SX = { height: 48, borderRadius: RADIUS.md, whiteSpace: 'nowrap' } as const;
const CLOSE_BUTTON_SX = { ...BUTTON_SX, flexShrink: 0, minWidth: { xs: 80, sm: 96 }, px: { xs: SPACE.card, sm: SPACE.section } } as const;
const PRIMARY_BUTTON_SX = { ...BUTTON_SX, flex: 1, minWidth: 0 } as const;
const NEVER_SX = { alignSelf: 'center', minHeight: 44, color: 'text.secondary', fontWeight: 400, textDecoration: 'underline', textUnderlineOffset: 3 } as const;
